/**
 * Shopify Connect Settings Page
 *
 * Admin UI for managing the Shopify integration: connect, sync, disconnect.
 *
 * PRD: squads/orchestrator/prds/2026-04-04-shopify-integration.md (Phase 1 Admin UI)
 * Backend routes: /api/integrations/shopify/* (Offering Studio)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ShoppingBag,
  Link2,
  Link2Off,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  Package,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import {
  shopifyIntegrationsService,
  type ShopifyConnectionStatus,
  type ShopifySyncHistoryEntry,
} from '@/services/shopifyIntegrationsService';

const STATUS_BADGES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  active: { variant: 'default', label: 'Connected' },
  syncing: { variant: 'secondary', label: 'Syncing' },
  error: { variant: 'destructive', label: 'Error' },
  disconnected: { variant: 'outline', label: 'Disconnected' },
};

export default function ShopifyConnect() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const siteId = selectedSite?.id;

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ShopifyConnectionStatus | null>(null);
  const [history, setHistory] = useState<ShopifySyncHistoryEntry[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await shopifyIntegrationsService.getStatus(siteId);
      setStatus(data);
    } catch (error) {
      // Backend not ready yet — show empty state
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const loadHistory = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await shopifyIntegrationsService.getSyncHistory(siteId);
      setHistory(data);
    } catch {
      setHistory([]);
    }
  }, [siteId]);

  useEffect(() => {
    setLoading(true);
    loadStatus();
    loadHistory();
  }, [loadStatus, loadHistory]);

  const handleConnect = async () => {
    if (!siteId) return;
    setConnecting(true);
    try {
      const { installUrl } = await shopifyIntegrationsService.getInstallUrl(siteId);
      window.open(installUrl, '_blank', 'width=600,height=700');
      toast({
        title: 'Shopify Authorization Started',
        description: 'Complete the installation in the popup window. This page will update automatically.',
      });
      // Poll for completion
      const interval = setInterval(async () => {
        try {
          const updated = await shopifyIntegrationsService.getStatus(siteId);
          if (updated.connected) {
            setStatus(updated);
            clearInterval(interval);
            toast({ title: 'Shopify Connected', description: `Connected to ${updated.shopName}` });
            loadHistory();
          }
        } catch { /* keep polling */ }
      }, 3000);
      // Stop polling after 5 minutes
      setTimeout(() => clearInterval(interval), 300000);
    } catch (error: any) {
      toast({
        title: 'Connection Failed',
        description: error.message || 'Failed to start Shopify install flow',
        variant: 'destructive',
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    if (!siteId) return;
    setSyncing(true);
    try {
      await shopifyIntegrationsService.triggerSync(siteId);
      toast({ title: 'Sync Started', description: 'Product catalog sync has been triggered.' });
      // Refresh status after a moment
      setTimeout(() => { loadStatus(); loadHistory(); }, 2000);
    } catch (error: any) {
      toast({
        title: 'Sync Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!siteId) return;
    setDisconnecting(true);
    try {
      await shopifyIntegrationsService.disconnect(siteId);
      setStatus(null);
      setHistory([]);
      setShowDisconnectDialog(false);
      toast({ title: 'Shopify Disconnected', description: 'The Shopify integration has been removed.' });
    } catch (error: any) {
      toast({
        title: 'Disconnect Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setDisconnecting(false);
    }
  };

  if (!siteId) {
    return (
      <div className="p-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Select a site from the header to manage Shopify integration.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const isConnected = status?.connected;

  return (
    <div className="p-8 max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShoppingBag className="h-6 w-6" />
          Shopify Integration
        </h1>
        <p className="text-muted-foreground mt-1">
          Connect your Shopify store to sync products, publish AEO content, and track performance.
        </p>
      </div>

      {!isConnected ? (
        /* ── Not Connected ── */
        <Card>
          <CardHeader>
            <CardTitle>Connect Your Shopify Store</CardTitle>
            <CardDescription>
              Install the PageMind app on your Shopify store to enable automatic product catalog sync,
              AEO content publishing via metafields, and performance tracking.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <Package className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Catalog Sync</p>
                    <p className="text-xs text-muted-foreground">Auto-import products via Shopify API</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RefreshCw className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Live Updates</p>
                    <p className="text-xs text-muted-foreground">Webhooks keep data in sync</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <ExternalLink className="h-5 w-5 text-purple-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Auto-Publish</p>
                    <p className="text-xs text-muted-foreground">AEO content pushed to metafields</p>
                  </div>
                </div>
              </div>
              <Button onClick={handleConnect} disabled={connecting} className="w-full md:w-auto">
                {connecting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting...</>
                ) : (
                  <><Link2 className="h-4 w-4 mr-2" /> Connect Shopify Store</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* ── Connected ── */
        <Tabs defaultValue="status">
          <TabsList>
            <TabsTrigger value="status">Store Status</TabsTrigger>
            <TabsTrigger value="history">Sync History</TabsTrigger>
          </TabsList>

          <TabsContent value="status" className="space-y-4">
            {/* Store info card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-lg">{status.shopName || status.shopDomain}</CardTitle>
                  <CardDescription>{status.shopDomain}</CardDescription>
                </div>
                <Badge variant={STATUS_BADGES[status.status]?.variant || 'outline'}>
                  {STATUS_BADGES[status.status]?.label || status.status}
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Products</p>
                    <p className="text-lg font-semibold">{(status.productCount ?? 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Connected</p>
                    <p className="text-sm">
                      {status.connectedAt ? new Date(status.connectedAt).toLocaleDateString() : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Last Sync</p>
                    <p className="text-sm flex items-center gap-1">
                      {status.lastSyncStatus === 'success' && <CheckCircle2 className="h-3 w-3 text-green-500" />}
                      {status.lastSyncStatus === 'error' && <AlertCircle className="h-3 w-3 text-red-500" />}
                      {status.lastSyncAt ? new Date(status.lastSyncAt).toLocaleString() : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Scopes</p>
                    <p className="text-sm">{status.scopes?.length || 0} granted</p>
                  </div>
                </div>

                {status.lastSyncError && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{status.lastSyncError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-2 mt-4">
                  <Button onClick={handleSync} disabled={syncing} variant="outline" size="sm">
                    {syncing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Syncing...</>
                    ) : (
                      <><RefreshCw className="h-4 w-4 mr-2" /> Sync Now</>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setShowDisconnectDialog(true)}
                  >
                    <Link2Off className="h-4 w-4 mr-2" /> Disconnect
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sync History</CardTitle>
                <CardDescription>Recent catalog synchronization runs</CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No sync history yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead>Deleted</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Started</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              {entry.status === 'success' && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                              {entry.status === 'error' && <AlertCircle className="h-3.5 w-3.5 text-red-500" />}
                              {entry.status === 'running' && <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />}
                              <span className="text-sm capitalize">{entry.status}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs capitalize">{entry.syncType}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">{entry.productsCreated}</TableCell>
                          <TableCell className="text-sm">{entry.productsUpdated}</TableCell>
                          <TableCell className="text-sm">{entry.productsDeleted}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {entry.duration ? `${(entry.duration / 1000).toFixed(1)}s` : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(entry.startedAt).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Disconnect confirmation dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Shopify Store?</DialogTitle>
            <DialogDescription>
              This will remove the connection to {status?.shopName || 'your Shopify store'}.
              Product data already imported will remain, but syncing and auto-publishing will stop.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisconnectDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDisconnect} disabled={disconnecting}>
              {disconnecting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Disconnecting...</>
              ) : (
                'Disconnect'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
